import { useState, useEffect, useCallback, useRef } from 'react';
import type { Snippet, Language, Difficulty } from '@codedrill/shared';

interface UseSnippetResult {
  snippet: Snippet | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// Module-level cache: fetch once for the lifetime of the page
let snippetCache: Snippet[] | null = null;
let fetchPromise: Promise<Snippet[]> | null = null;

async function loadSnippets(): Promise<Snippet[]> {
  if (snippetCache) return snippetCache;
  if (!fetchPromise) {
    fetchPromise = fetch('/snippets.json')
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load snippets (${r.status})`);
        return r.json() as Promise<Snippet[]>;
      })
      .then((data) => {
        snippetCache = data;
        return data;
      })
      .catch((err) => {
        fetchPromise = null; // allow retry on next call
        throw err;
      });
  }
  return fetchPromise;
}

function pickRandom(
  snippets: Snippet[],
  language: Language | null,
  difficulty: Difficulty | null,
): Snippet | null {
  let pool = snippets;
  if (language)   pool = pool.filter((s) => s.language === language);
  if (difficulty) pool = pool.filter((s) => s.difficulty === difficulty);
  if (pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)] ?? null;
}

export function useSnippet(
  language: Language | null,
  difficulty: Difficulty | null,
): UseSnippetResult {
  const [snippet, setSnippet] = useState<Snippet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counter, setCounter] = useState(0);

  // Keep a stable ref to the last-picked ID so refresh always picks a different one
  const lastIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadSnippets()
      .then((all) => {
        if (cancelled) return;
        // Avoid serving the same snippet twice in a row
        let picked = pickRandom(all, language, difficulty);
        if (picked && picked.id === lastIdRef.current && all.length > 1) {
          picked = pickRandom(all, language, difficulty);
        }
        if (!picked) {
          setError('No snippets found for these filters');
          setSnippet(null);
        } else {
          lastIdRef.current = picked.id;
          setSnippet(picked);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load snippets');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [language, difficulty, counter]);

  const refresh = useCallback(() => setCounter((c) => c + 1), []);

  return { snippet, loading, error, refresh };
}
